/**
 * The AI wizard's step machine: setup → prompt → paste → review → done.
 * Holds the generation options, the fetched prompt, and the review items;
 * the paste gate checks BOTH the envelope and the chosen-target match.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type {
  FlowLayer,
  FlowOptimizerLibraryPromptTarget,
  FlowOptimizerLibraryProvider,
} from '@trafficpulse/shared';

import { useFlowOptimizerMetadata } from '@/features/flow-optimizer/hooks';

import { fetchPromptPreview } from '../api/client/ai-api';
import { parseLibraryJsonDetailed } from '../envelope/parse-library-json';
import type { LibraryParseFailure } from '../envelope/parse-library-json';
import { decomposeLibraryPayload } from '../validate/review-items';
import type { ReviewItem } from '../validate/review-items';

export type WizardStep = 'setup' | 'prompt' | 'paste' | 'review' | 'done';

export interface WizardOptions {
  provider: FlowOptimizerLibraryProvider;
  campaignId: string;
  target: FlowOptimizerLibraryPromptTarget;
  /** Stat windows — also the dateRange values the AI must use on groups. */
  dateRanges: string[];
  from?: string;
  to?: string;
  layers: FlowLayer[];
  objective: string;
  /** Ceilings for the ask — the AI may return fewer, never more. */
  maxFormulas: number;
  maxRules: number;
  /** Client's rule behavior: swap losers out, or adjust in place only. */
  ruleStyle: 'rotate' | 'in-place';
}

const TARGET_TYPES: Record<FlowOptimizerLibraryPromptTarget, string[]> = {
  formulas: ['formula', 'group'],
  rules: ['rule-set'],
  package: ['package'],
};

const FAILURE_MESSAGES: Record<LibraryParseFailure, string> = {
  'not-json':
    'Invalid JSON — the pasted text does not parse. Copy the model’s full JSON output (a ```json fence around it is fine).',
  'wrong-kind':
    'This JSON is not a TrafficPulse payload — it needs "kind": "tp-flow-optimizer".',
  'unsupported-version': 'Unsupported payload version — ask the model for "version": 2.',
  'unknown-type':
    'Unknown payload type — expected formula, rule-set or package.',
  'bad-data':
    'The payload’s data is incomplete — formulas need "groups", rule sets need "excludeRules".',
};

export function useAiWizard(initial: {
  provider: FlowOptimizerLibraryProvider;
  campaignId: string;
}) {
  const [step, setStep] = useState<WizardStep>('setup');
  const [options, setOptions] = useState<WizardOptions>({
    provider: initial.provider,
    campaignId: initial.campaignId,
    target: 'package',
    dateRanges: ['last-7-days'],
    layers: ['paths', 'landers', 'offers'],
    objective: '',
    maxFormulas: 4,
    maxRules: 5,
    ruleStyle: 'rotate',
  });
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [pasteError, setPasteError] = useState<string | null>(null);
  const metadata = useFlowOptimizerMetadata(options.provider);

  const preview = useMutation({
    mutationFn: fetchPromptPreview,
    onSuccess: () => setStep('prompt'),
  });

  const validationCtx = useMemo(() => {
    const meta = preview.data?.meta;
    return {
      metricIds: metadata.data
        ? new Set(metadata.data.metrics.map((m) => m.id))
        : undefined,
      dateRangePresets: metadata.data
        ? new Set(metadata.data.dateRanges.map((r) => r.id))
        : undefined,
      tagNames: meta ? new Set(meta.tagNames) : undefined,
      ruleStyle: options.ruleStyle,
    };
  }, [metadata.data, preview.data?.meta, options.ruleStyle]);

  const patch = (next: Partial<WizardOptions>) =>
    setOptions((prev) => ({ ...prev, ...next }));

  const buildPrompt = () => preview.mutate(options);

  const reviewPaste = (text: string) => {
    const result = parseLibraryJsonDetailed(text);
    if (!result.ok) {
      setPasteError(FAILURE_MESSAGES[result.failure]);
      return;
    }
    const parsed = result.payload;
    if (!TARGET_TYPES[options.target].includes(parsed.type)) {
      setPasteError(
        `This payload contains a ${parsed.type}, but you chose ${options.target} — go back or paste a matching payload.`,
      );
      return;
    }
    setPasteError(null);
    setItems(decomposeLibraryPayload(parsed, validationCtx));
    setStep('review');
  };

  const setAccepted = (id: string, accepted: boolean) =>
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const blocked = item.issues.some((issue) => issue.level === 'error');
        return { ...item, accepted: accepted && !blocked };
      }),
    );

  const setAllAccepted = (accepted: boolean) =>
    setItems((prev) =>
      prev.map((item) => ({
        ...item,
        accepted:
          accepted && !item.issues.some((issue) => issue.level === 'error'),
      })),
    );

  return {
    step,
    setStep,
    options,
    patch,
    buildPrompt,
    preview,
    pasteError,
    reviewPaste,
    items,
    setAccepted,
    setAllAccepted,
  };
}

export type AiWizardState = ReturnType<typeof useAiWizard>;
